// Amplitude Events & Properties System

export const AMPLITUDE_EVENTS = {
  // Search Related Events
  SEARCH: {
    INITIATED: 'Search Initiated',
    INPUT_UPDATED: 'Search Input Updated',
    DROPDOWN_SELECTED: 'Search Dropdown Option Selected',
    LOCATION_UPDATED: 'Location Input Updated',
    LOCATION_DROPDOWN_SELECTED: 'Location Dropdown Option Selected',
    SPECIALTY_CARD_CLICKED: 'Specialty Card Clicked',
  },
  // Geolocation Events
  GEOLOCATION: {
    SEARCH_BAR_ICON_CLICKED: 'Search Bar Geolocation Icon Clicked',
    SIDE_FILTER_BUTTON_CLICKED: 'Side Filter Geolocation Button Clicked',
    SUCCESS: 'Geolocation Success',
    ERROR: 'Geolocation Error',
  },
  // Physician Interaction Events
  PHYSICIAN: {
    PROFILE_LINK_CLICKED: 'Physician Profile Link Clicked',
    REQUEST_APPOINTMENT: 'Request Appointment Button Clicked',
    PHONE_BUTTON_CLICKED: 'Phone Number Button Clicked',
  },
  // Booking Events
  BOOKING: {
    ONLINE_BOOKING_CLICKED: 'Book Online Button Clicked',
    PHONE_BOOKING_CLICKED: 'Phone Number Button Clicked',
    SCHEDULE_APPOINTMENT_BUTTON_CLICKED: 'Schedule Appointment Button Clicked',
  },
  // Validation Events
  VALIDATION: {
    SEARCH_ERROR: 'Search Validation Error',
    LOCATION_ERROR: 'Location Validation Error',
  },
  // Profile Events
  PROFILE: {
    UPDATE_PROFILE: 'User Profile Updated',
    PHOTO_UPDATED: 'User Profile Photo Updated',
  },
} as const

// Type for tracking functions
type TrackingFunction = (
  eventName: string,
  eventProperties: Record<string, any>
) => void

interface GeolocationProps {
  'Current Location Exists': boolean
  Latitude: number
  Longitude: number
  'Error Message': string
}

interface PhysicianEventProps {
  npi: string
  full_name: string
  specialty: string
  city: string
  state: string
  booking_url?: string
  telehealth_appointment?: string
  position: number | string
}

interface SearchInputProps {
  'Current Search Bar Category': string
  'Input Value': string
  'Dropdown Option Selected': boolean
}

interface LocationInputProps {
  'Input Value': string
  'Dropdown Option Selected': boolean
}

interface ValidationErrorProps {
  'Error Message': string
  'Input Value': string
  'Input Type': string
}

// Function to format physician event properties
const formatPhysicianEventProps = (physician: PhysicianEventProps) => ({
  'Physician NPI': physician.npi,
  'Physician Full Name': physician.full_name,
  'Physician Specialty': physician.specialty,
  Location: `${physician.city}, ${physician.state}`,
  'Physician Position': physician.position,
})

// Function to format search event properties
const formatSearchEventProps = (params: {
  'Search Bar Category': string
  'Local Autocomplete Value': string | null
  'Local Location Value': string | null
  'Address Data': any
  Options: any[]
  'Location Options': any[]
  'Is Navigating Dropdown': boolean
}) => ({
  'Search Type': params['Search Bar Category'],
  'Search Value': params['Local Autocomplete Value'],
  'Location Value': params['Local Location Value'],
  'Has Search Value': !!params['Local Autocomplete Value'],
  'Has Location': !!params['Local Location Value'],
  'Used Geolocation': !!params['Address Data']?.fullAddress,
  'Dropdown Option Selected': params.Options
    ? params.Options.length > 0
    : false,
})

// Updated Search Events
export const trackSearchEvents = {
  searchInitiated:
    (trackAmplitudeEvent: TrackingFunction) =>
    (params: Parameters<typeof formatSearchEventProps>[0]) => {
      trackAmplitudeEvent(
        AMPLITUDE_EVENTS.SEARCH.INITIATED,
        formatSearchEventProps(params)
      )
    },

  searchInputUpdated: (trackAmplitudeEvent: TrackingFunction) => ({
    forSearch: (params: SearchInputProps) => {
      trackAmplitudeEvent(AMPLITUDE_EVENTS.SEARCH.INPUT_UPDATED, {
        'Input Type': params['Current Search Bar Category'],
        'Input Value': params['Input Value'],
        'Dropdown Option Selected': params['Dropdown Option Selected'],
      })
    },
    forLocation: (params: LocationInputProps) => {
      trackAmplitudeEvent(AMPLITUDE_EVENTS.SEARCH.LOCATION_UPDATED, {
        'Input Type': 'location',
        'Input Value': params['Input Value'],
        'Dropdown Option Selected': params['Dropdown Option Selected'],
      })
    },
  }),

  searchDropdownSelected:
    (trackAmplitudeEvent: TrackingFunction) =>
    (params: {
      'Input Type': string
      'Selected Value': string
      'Previous Value': string
    }) => {
      trackAmplitudeEvent(
        params['Input Type'] === 'location'
          ? AMPLITUDE_EVENTS.SEARCH.LOCATION_DROPDOWN_SELECTED
          : AMPLITUDE_EVENTS.SEARCH.DROPDOWN_SELECTED,
        params
      )
    },

  specialtyCardClicked:
    (trackAmplitudeEvent: TrackingFunction) =>
    (params: { 'Specialty Title': string; 'Specialty Value': string }) => {
      trackAmplitudeEvent(
        AMPLITUDE_EVENTS.SEARCH.SPECIALTY_CARD_CLICKED,
        params
      )
    },
}

// Profile Events
export const trackProfileEvents = {
  updateProfile:
    (trackAmplitudeEvent: TrackingFunction) =>
    (params: { 'Profile Updated': any }) => {
      trackAmplitudeEvent(AMPLITUDE_EVENTS.PROFILE.UPDATE_PROFILE, params)
    },
}

export const trackProfilePhotoEvents = {
  photoUpdated:
    (trackAmplitudeEvent: TrackingFunction) =>
    (params: { 'Photo Updated': any; 'Profile Updated': any }) => {
      trackAmplitudeEvent(AMPLITUDE_EVENTS.PROFILE.PHOTO_UPDATED, params)
    },
}
// Geolocation Events
export const trackGeolocationEvents = {
  searchBarIconClicked:
    (trackAmplitudeEvent: TrackingFunction) =>
    ({
      'Current Location Exists': currentLocationExists,
    }: Pick<GeolocationProps, 'Current Location Exists'>) => {
      trackAmplitudeEvent(
        AMPLITUDE_EVENTS.GEOLOCATION.SEARCH_BAR_ICON_CLICKED,
        {
          'Current Location Exists': currentLocationExists,
        }
      )
    },
  clicked:
    (trackAmplitudeEvent: TrackingFunction) =>
    ({
      'Current Location Exists': currentLocationExists,
    }: Pick<GeolocationProps, 'Current Location Exists'>) => {
      trackAmplitudeEvent(
        AMPLITUDE_EVENTS.GEOLOCATION.SIDE_FILTER_BUTTON_CLICKED,
        {
          'Current Location Exists': currentLocationExists,
        }
      )
    },

  success:
    (trackAmplitudeEvent: TrackingFunction) =>
    ({
      Latitude: latitude,
      Longitude: longitude,
    }: Pick<GeolocationProps, 'Latitude' | 'Longitude'>) => {
      trackAmplitudeEvent(AMPLITUDE_EVENTS.GEOLOCATION.SUCCESS, {
        Latitude: latitude,
        Longitude: longitude,
      })
    },

  error:
    (trackAmplitudeEvent: TrackingFunction) =>
    ({
      'Error Message': errorMessage,
    }: Pick<GeolocationProps, 'Error Message'>) => {
      trackAmplitudeEvent(AMPLITUDE_EVENTS.GEOLOCATION.ERROR, {
        'Error Message': errorMessage,
      })
    },
}

// Physician Events
export const trackPhysicianEvents = {
  profileLinkClicked:
    (trackAmplitudeEvent: TrackingFunction) =>
    (physician: PhysicianEventProps) => {
      trackAmplitudeEvent(
        AMPLITUDE_EVENTS.PHYSICIAN.PROFILE_LINK_CLICKED,
        formatPhysicianEventProps(physician)
      )
    },

  requestAppointment:
    (trackAmplitudeEvent: TrackingFunction) =>
    (physician: PhysicianEventProps) => {
      trackAmplitudeEvent(
        AMPLITUDE_EVENTS.PHYSICIAN.REQUEST_APPOINTMENT,
        formatPhysicianEventProps(physician)
      )
    },

  phoneButtonClicked:
    (trackAmplitudeEvent: TrackingFunction) =>
    (physician: PhysicianEventProps) => {
      trackAmplitudeEvent(
        AMPLITUDE_EVENTS.PHYSICIAN.PHONE_BUTTON_CLICKED,
        formatPhysicianEventProps(physician)
      )
    },
}

// Booking Events
export const trackBookingEvents = {
  scheduleAppointmentClicked:
    (trackAmplitudeEvent: TrackingFunction) =>
    (physician: PhysicianEventProps) => {
      trackAmplitudeEvent(
        AMPLITUDE_EVENTS.BOOKING.SCHEDULE_APPOINTMENT_BUTTON_CLICKED,
        formatPhysicianEventProps(physician)
      )
    },
  onlineBookingClicked:
    (trackAmplitudeEvent: TrackingFunction) =>
    (physician: PhysicianEventProps) => {
      trackAmplitudeEvent(
        AMPLITUDE_EVENTS.BOOKING.ONLINE_BOOKING_CLICKED,
        formatPhysicianEventProps(physician)
      )
    },

  phoneBookingClicked:
    (trackAmplitudeEvent: TrackingFunction) =>
    (physician: PhysicianEventProps) => {
      trackAmplitudeEvent(
        AMPLITUDE_EVENTS.BOOKING.PHONE_BOOKING_CLICKED,
        formatPhysicianEventProps(physician)
      )
    },
}

// Validation error tracking functions
export const trackValidationEvents = {
  searchError:
    (trackAmplitudeEvent: TrackingFunction) =>
    ({
      'Error Message': errorMessage,
      'Input Value': inputValue,
      'Input Type': inputType,
    }: ValidationErrorProps) => {
      trackAmplitudeEvent(AMPLITUDE_EVENTS.VALIDATION.SEARCH_ERROR, {
        'Error Message': errorMessage,
        'Input Value': inputValue,
        'Input Type': inputType,
        'Validation Type': 'Search',
      })
    },

  locationError:
    (trackAmplitudeEvent: TrackingFunction) =>
    ({
      'Error Message': errorMessage,
      'Input Value': inputValue,
      'Input Type': inputType,
    }: ValidationErrorProps) => {
      trackAmplitudeEvent(AMPLITUDE_EVENTS.VALIDATION.LOCATION_ERROR, {
        'Error Message': errorMessage,
        'Input Value': inputValue,
        'Input Type': inputType,
        'Validation Type': 'Location',
      })
    },
}
